import { useCallback, useState } from "react";
import { add, differenceInCalendarDays, format, type Duration } from "date-fns";
import { ContractData, ScheduledPayment } from "@/pages/contracts/types";
import { DuplicateRevenueItemPayload } from "@/pages/contracts/hooks";
import { DraftRevenueItem } from "@/pages/contracts/components/termsDraft/types";
import {
  toApiDate,
  toApiDateOrNull
} from "@/pages/contracts/components/termsDraft/apiDates";
import { useTermsDraft } from "@/pages/contracts/components/termsDraft/useTermsDraft";
import { toLocalDate } from "@/utils/datetime";
import { DateShift } from "./types";

const SHIFTED_DATE_FIELDS = [
  "serviceStartDate",
  "serviceEndDate",
  "billingStartDate",
  "billingEndDate",
  "customOptOutDate"
] as const;

const shiftISODate = (
  value: string | null,
  duration: Duration
): string | null => {
  if (!value) return null;
  // Shift the calendar day itself. Adding to a midnight-UTC instant does the
  // arithmetic in local time, so crossing a daylight-saving boundary moves the
  // UTC day out from under it — a ten-day shift off 1 March lands on the 10th
  // rather than the 11th anywhere behind UTC.
  const shiftedDay = format(add(toLocalDate(value), duration), "yyyy-MM-dd");
  return value.includes("T") ? `${shiftedDay}T00:00:00Z` : shiftedDay;
};

const shiftItemDates = (
  item: DraftRevenueItem,
  duration: Duration
): DraftRevenueItem => {
  // The payment rows move with their line, or the schedule lands outside the
  // shifted billing period.
  const shifted = {
    ...item,
    scheduledPayments: item.scheduledPayments.map((payment) => ({
      ...payment,
      billingDate: shiftISODate(
        payment.billingDate,
        duration
      ) as ScheduledPayment["billingDate"]
    }))
  };
  SHIFTED_DATE_FIELDS.forEach((field) => {
    (shifted as unknown as Record<string, string | null>)[field] = shiftISODate(
      item[field],
      duration
    );
  });
  return shifted;
};

export const useDuplicateContractDraft = (sourceContract: ContractData) => {
  const draft = useTermsDraft(sourceContract.revenueItems);
  const { items, replaceItems } = draft;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toPayloadItems = useCallback(
    (): DuplicateRevenueItemPayload[] =>
      items
        .filter((item) => !item.draftDeleted)
        .map((item, index) => ({
          serviceItemId: item.serviceItemId,
          serviceItemName: item.serviceItemName,
          serviceItemPricingId: item.serviceItemPricingId,
          serviceStartDate: toApiDateOrNull(item.serviceStartDate),
          serviceEndDate: toApiDateOrNull(item.serviceEndDate),
          // Unlike the discount pair, create stores auto-renewal as a boolean and reads null as false.
          autoRenewal: item.autoRenewal ?? false,
          quantity: item.quantity,
          pricePerUnit: item.pricePerUnit,
          // Carries a source line's unset discount over as unset rather than as "$0".
          isDiscountPercentage: item.isDiscountPercentage ?? null,
          discountAmount: item.discountAmount ?? null,
          total: item.total,
          totalLocal: item.totalLocal,
          billingStartDate: toApiDateOrNull(item.billingStartDate),
          billingEndDate: toApiDateOrNull(item.billingEndDate),
          customBillingFrequency: item.customBillingFrequency,
          customServiceDuration: item.customServiceDuration,
          customOptOutDate: toApiDateOrNull(item.customOptOutDate),
          customDuration: item.customDuration,
          eqPaymentsNumOfPayments: item.eqPaymentsNumOfPayments,
          eqPaymentsBillingFrequency: item.eqPaymentsBillingFrequency,
          itemOrder: index,
          scheduledPayments: item.scheduledPayments.map((payment) => ({
            paymentNumber: payment.paymentNumber,
            billingDate: toApiDate(payment.billingDate),
            amount: payment.amount ?? 0,
            percentage: payment.percentage ?? 0
          }))
        })),
    [items]
  );

  const shiftDates = useCallback(
    (ids: string[], shift: DateShift) => {
      const duration: Duration = { [shift.unit]: shift.amount };
      replaceItems(
        items.map((item) =>
          ids.includes(item.id) ? shiftItemDates(item, duration) : item
        )
      );
    },
    [items, replaceItems]
  );

  const setStartDate = useCallback(
    (ids: string[], startDate: string) => {
      replaceItems(
        items.map((item) => {
          if (!ids.includes(item.id) || !item.serviceStartDate) return item;
          // Both ends have to be read the same way the shift below applies
          // them. Measuring the gap between instants while moving calendar
          // days puts the line a day out wherever the two disagree.
          const days = differenceInCalendarDays(
            toLocalDate(startDate),
            toLocalDate(item.serviceStartDate)
          );
          return shiftItemDates(item, { days });
        })
      );
    },
    [items, replaceItems]
  );

  const toggleSelect = useCallback(
    (id: string) =>
      setSelectedIds((current) =>
        current.includes(id)
          ? current.filter((selectedId) => selectedId !== id)
          : [...current, id]
      ),
    []
  );

  const setSelected = useCallback((ids: string[]) => setSelectedIds(ids), []);

  // Deleting clears the selection; the bulk-actions bar acts on ids.
  const deleteItems = useCallback(
    (ids: string[]) => {
      draft.deleteItems(ids);
      setSelectedIds([]);
    },
    [draft]
  );

  return {
    items,
    selectedIds,
    baseline: draft.baseline,
    toggleSelect,
    setSelected,
    deleteItems,
    restoreItem: draft.restoreItem,
    addItem: draft.addItem,
    updateItem: draft.updateItem,
    replaceItems,
    shiftDates,
    setStartDate,
    toPayloadItems
  };
};

export type DuplicateContractDraft = ReturnType<
  typeof useDuplicateContractDraft
>;
